import React, { FunctionComponent, memo, useRef } from "react";
import { Grid, Box, FormControlLabel, RadioGroup, Radio } from "@mui/material";
import { Input, Singleselect, ListOptionBase } from "@shared/components";
import ExtractionAwareInput from "@investorRequest/components/ExtractionAwareInput";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import { FIELD_TITLES } from "@investorRequest/constants/fieldTooltips";
import InvestorRequestFormTitle from "@investorRequest/components/InvestorRequestFormTitle";
import { validationMessages } from "@investorRequest/constants/validationMessages";
import {
  sanitizeAsciiAlnum,
  sanitizeAsciiName,
  isOverLimit,
  maxLengthMessage,
} from "@investorRequest/utils/SpecificValidations";
import { useC2DSErrors } from "@investorRequest/hooks/useC2DSFieldError";
import C2DSFieldErrorMessages from "@investorRequest/components/C2DSFieldErrorMessages";

interface Props {
  yesNoValue: string; // "yes" | "no" | ""
  swiftOrAba: string;
  bankName: string;
  countriesOptions: ListOptionBase[];
  selectedCountry?: ListOptionBase;
  onSelectCountry(option: ListOptionBase): void;
  onChangeYesNo(value: string): void;
  onChangeSwiftOrAba(value: string): void;
  onChangeBankName(value: string): void;
  showErrors: boolean;
}

const MAX_INTERMEDIARY_ID_LENGTH = 12;

// IR-UI-Revamp_V1: Restored Yes/No radio buttons for intermediary instructions
const IntermediaryFields: FunctionComponent<Props> = ({
  yesNoValue,
  swiftOrAba,
  bankName,
  countriesOptions,
  selectedCountry,
  onSelectCountry,
  onChangeYesNo,
  onChangeSwiftOrAba,
  onChangeBankName,
  showErrors,
}) => {
  const radioTopRef = useRef<HTMLDivElement | null>(null);
  const fieldsStartRef = useRef<HTMLDivElement | null>(null);

  // LPC-10289: C2DS validation errors
  const { hasFieldError, clearFieldError } = useC2DSErrors();

  // LPC-10289: Cache C2DS error states to ensure consistency between error prop and message display
  const hasIntermediarySwiftC2DSError = hasFieldError("intermediarySwiftOrAba");

  const showSwiftOrAbaError = showErrors && yesNoValue === "yes" && !swiftOrAba;

  return (
    <>
      <InvestorRequestFormTitle text="Do these wire instructions contain intermediary instructions? (Yes/No)" />
      <Grid item xs={12}>
        <div ref={radioTopRef} />
        <RadioGroup row value={yesNoValue || ""}>
          <FormControlLabel
            value="yes"
            control={<Radio size="small" />}
            label="Yes"
            onClick={() => onChangeYesNo("yes")}
          />
          <FormControlLabel
            value="no"
            control={<Radio size="small" />}
            label="No"
            onClick={() => onChangeYesNo("no")}
          />
        </RadioGroup>
      </Grid>
      {yesNoValue === "yes" && (
        <>
          <div ref={fieldsStartRef} />
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.IntermediaryAbaSwift}>
              <ExtractionAwareInput
                fieldName="intermediarySwiftOrAba"
                dense={false}
                placeholder="*Intermediary ABA / SWIFT"
                variant="light"
                size="large"
                value={swiftOrAba}
                onValueChange={(v) => {
                  // LPC-10289: Clear C2DS error when user modifies field
                  clearFieldError("intermediarySwiftOrAba");
                  onChangeSwiftOrAba(sanitizeAsciiAlnum(v));
                }}
                sanitizeOnPaste={(t) => sanitizeAsciiAlnum(t)}
                error={
                  showSwiftOrAbaError ||
                  isOverLimit(swiftOrAba, MAX_INTERMEDIARY_ID_LENGTH) ||
                  hasIntermediarySwiftC2DSError
                }
              />
            </FieldTooltip>
            {showSwiftOrAbaError && (
              <Box mt={1} color="warning.main" fontSize={12}>
                {validationMessages.intermediary.swiftOrAba.required}
              </Box>
            )}
            {isOverLimit(swiftOrAba, MAX_INTERMEDIARY_ID_LENGTH) && (
              <Box mt={1} color="warning.main" fontSize={12}>
                {maxLengthMessage("Intermediary ABA / SWIFT", MAX_INTERMEDIARY_ID_LENGTH)}
              </Box>
            )}
            {/* LPC-10289: C2DS validation error */}
            <C2DSFieldErrorMessages fieldKey="intermediarySwiftOrAba" />
          </Grid>
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.IntermediaryBankCountry}>
              <Singleselect
                options={countriesOptions}
                selectedOption={selectedCountry}
                onSelect={onSelectCountry}
                label={selectedCountry?.label || "Bank Country"}
                variant="contained"
                size="large"
                withSearch
                testId="intermediary-bank-country-select"
              />
            </FieldTooltip>
          </Grid>
          {/* Bank Name field (optional; no C2DS character limit for intermediary bank name) */}
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.IntermediaryBankName}>
              <Input
                dense={false}
                placeholder="Bank Name"
                variant="light"
                size="large"
                value={bankName}
                onChange={(v) => onChangeBankName(sanitizeAsciiName(v))}
                sanitizeOnPaste={(t) => sanitizeAsciiName(t)}
                error={false}
              />
            </FieldTooltip>
          </Grid>
        </>
      )}
    </>
  );
};

export default memo(IntermediaryFields);
